import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Play,
  Sparkles,
} from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import { Spinner } from '@/components/spinner';
import { TODAY_VISIBLE, todaySummary, type TodayItem } from '@/lib/student/today';

/**
 * « À faire » — premier bloc de l'Accueil élève : devoirs (les plus urgents
 * d'abord), exercice à reprendre, diagnostic à faire. Une ligne = une action.
 * Rien à faire → un seul raccourci vers l'entraînement (pas de liste vide).
 */
export function StudentTodaySection({
  items,
  isLoading = false,
  pendingKey,
  onLaunch,
  title = 'À faire',
  hideWhenEmpty = false,
}: {
  items: readonly TodayItem[];
  title?: string;
  /** Rien à faire → aucune section (page S'entraîner, déjà pleine d'entraînement). */
  hideWhenEmpty?: boolean;
  isLoading?: boolean;
  /** Ligne en cours d'ouverture (spinner sur son bouton). */
  pendingKey: string | null;
  /** Ouvre l'exercice d'un devoir ou d'une reprise. */
  onLaunch: (item: Extract<TodayItem, { kind: 'homework' | 'resume' }>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, TODAY_VISIBLE);
  const hidden = items.length - visible.length;
  if (hideWhenEmpty && !isLoading && items.length === 0) return null;

  return (
    <section aria-labelledby="today-title" className="space-y-2">
      <div>
        <h2 id="today-title" className="font-display text-ink-900 text-lg font-bold">
          {title}
        </h2>
        {!isLoading && <p className="text-ink-500 text-sm">{todaySummary(items)}</p>}
      </div>

      {isLoading ? (
        <div className="border-ink-100 h-[4.5rem] animate-pulse rounded-2xl border bg-white" />
      ) : items.length === 0 ? (
        <Link
          to="/student/revise"
          className="border-ink-100 flex min-h-[2.75rem] items-center gap-3 rounded-2xl border bg-white p-4 active:scale-[0.98]"
        >
          <span className="bg-accent-100 ocean:bg-accent-500/20 text-accent-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
            <Sparkles className="h-5 w-5" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="text-ink-900 block font-semibold">S’entraîner</span>
            <span className="text-ink-500 block text-sm">
              Exercice du jour, warmup, automatismes
            </span>
          </span>
          <ChevronRight className="text-ink-400 h-5 w-5 shrink-0" aria-hidden />
        </Link>
      ) : (
        <ul className="border-ink-100 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
          {visible.map((item) => (
            <li key={item.key}>
              <TodayRow
                item={item}
                pending={pendingKey === item.key}
                disabled={pendingKey !== null}
                onLaunch={onLaunch}
              />
            </li>
          ))}
          {(hidden > 0 || expanded) && items.length > TODAY_VISIBLE && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-primary-600 flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 text-sm font-semibold"
              >
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')}
                  aria-hidden
                />
                {expanded ? 'Réduire' : `Voir ${hidden === 1 ? 'l’autre' : `les ${hidden} autres`}`}
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

const DUE_TONE = {
  late: 'bg-red-50 text-red-700 ocean:bg-[#4a1520] ocean:text-[#fecaca]',
  today: 'bg-amber-50 text-amber-800 ocean:bg-[#3a2f0c] ocean:text-[#fde68a]',
  soon: 'bg-amber-50 text-amber-800 ocean:bg-[#3a2f0c] ocean:text-[#fde68a]',
  later: 'bg-ink-50 text-ink-600 ocean:bg-white/5',
  none: 'bg-ink-50 text-ink-600 ocean:bg-white/5',
} as const;

function TodayRow({
  item,
  pending,
  disabled,
  onLaunch,
}: {
  item: TodayItem;
  pending: boolean;
  disabled: boolean;
  onLaunch: (item: Extract<TodayItem, { kind: 'homework' | 'resume' }>) => void;
}) {
  if (item.kind === 'diagnostic') {
    return (
      <Link
        to="/student/diagnostic"
        className="flex min-h-[2.75rem] items-center gap-3 p-3 pr-4 active:scale-[0.99]"
      >
        <RowIcon tone="bg-primary-600 text-white">
          <ClipboardCheck className="h-5 w-5" aria-hidden />
        </RowIcon>
        <span className="min-w-0 flex-1">
          <span className="text-ink-900 block text-sm font-semibold">
            {item.started ? 'Termine ton diagnostic' : 'Fais ton diagnostic'}
          </span>
          <span className="text-ink-500 block text-xs">
            {item.started
              ? `${item.answered} question${item.answered > 1 ? 's' : ''} sur ${item.total}`
              : item.total > 0
                ? `${item.total} questions pour savoir où tu en es`
                : // questions tirées au 1er démarrage : leur nombre n'est pas encore connu
                  'Une quinzaine de questions pour savoir où tu en es'}
          </span>
        </span>
        <ActionPill label={item.started ? 'Reprendre' : 'Commencer'} />
      </Link>
    );
  }

  const isHomework = item.kind === 'homework';
  const title = isHomework ? item.homework.title : item.exercise.exerciseTitle;
  const progress = isHomework ? item.resumeProgress : item.progress;
  const resuming = !isHomework || item.resumeProgress !== null;
  const late = isHomework && item.due === 'late';

  return (
    <button
      type="button"
      onClick={() => onLaunch(item)}
      disabled={disabled}
      aria-busy={pending || undefined}
      className="flex min-h-[2.75rem] w-full items-center gap-3 p-3 pr-4 text-left active:scale-[0.99] disabled:cursor-default"
    >
      <RowIcon
        tone={
          late
            ? 'bg-red-100 text-red-600 ocean:bg-[#4a1520] ocean:text-[#fca5a5]'
            : isHomework
              ? 'bg-[#fde68a] text-[#5b4300] ocean:bg-[#3a2f0c] ocean:text-[#f7d069]'
              : 'bg-primary-100 text-primary-600 ocean:bg-[#123a7a] ocean:text-[#bfdbfe]'
        }
      >
        {isHomework ? (
          <ClipboardList className="h-5 w-5" aria-hidden />
        ) : (
          <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden />
        )}
      </RowIcon>
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 line-clamp-2 text-sm font-semibold">
          <MathText>{title}</MathText>
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {isHomework ? (
            <>
              <span className="text-ink-500">
                {item.homework.source === 'class' ? 'Devoir · ' : 'Devoir de '}
                {item.homework.fromLabel}
              </span>
              {item.dueText && (
                <span className={cn('rounded-full px-2 py-0.5 font-semibold', DUE_TONE[item.due])}>
                  {item.dueText}
                </span>
              )}
            </>
          ) : (
            <span className="text-ink-500">
              En cours
              {item.exercise.chapterTitle ? ` · ${item.exercise.chapterTitle}` : ''}
            </span>
          )}
        </span>
        {progress !== null && progress > 0 && (
          <span className="bg-primary-50 ocean:bg-white/10 mt-1.5 block h-1.5 overflow-hidden rounded-full">
            <span
              className="bg-primary-500 block h-full rounded-full"
              // Largeur dynamique (progression) : seule valeur non exprimable en classe.
              style={{ width: `${progress}%` }}
            />
          </span>
        )}
      </span>
      <ActionPill
        label={pending ? 'Ouverture…' : resuming ? 'Reprendre' : 'Lancer'}
        pending={pending}
      />
    </button>
  );
}

function RowIcon({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', tone)}>
      {children}
    </span>
  );
}

function ActionPill({ label, pending = false }: { label: string; pending?: boolean }) {
  return (
    <span className="bg-primary-500 flex h-9 shrink-0 items-center gap-1 rounded-full px-3.5 text-sm font-bold text-white shadow-[0_4px_10px_rgba(5,102,242,0.25)]">
      {pending ? <Spinner /> : null}
      {label}
      {!pending && <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
    </span>
  );
}
